import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, UserPlus, Shield, Activity, X, Mail, User, Save, Trash2 } from 'lucide-react';
import { AdminLayout } from './components/AdminLayout';
import { AdminTopBar } from './components/AdminTopBar';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { userStorage, type StoredUser } from '../../utils/userStorage';
import { DeleteConfirmationModal } from './components/DeleteConfirmationModal';
import { authAPI } from '../../../services/api';

export function AdminUsers() {
    const navigate = useNavigate();
    const [users, setUsers] = useState<StoredUser[]>([]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<StoredUser | null>(null);
    const [editForm, setEditForm] = useState<{
        name: string;
        email: string;
        role: StoredUser['role'];
        status: StoredUser['status'];
    }>({
        name: '',
        email: '',
        role: 'User',
        status: 'Active'
    });
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        itemId: '',
        itemName: '',
        isDeleting: false
    });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const adminAuth = localStorage.getItem('adminAuth');
        if (!adminAuth) {
            navigate('/admin');
        }
        loadUsers();
    }, [navigate]);

    const loadUsers = async () => {
        try {
            setIsLoading(true);
            
            // Always fetch admins from MongoDB via API
            const response = await authAPI.getAdmins();
            
            // Map MongoDB admin users to StoredUser interface
            const apiUsers = response.data.map((user: any) => ({
                id: user._id || user.id,
                name: user.name || user.username,
                email: user.email,
                role: (user.role || 'User') as StoredUser['role'],
                status: (user.status || 'Active') as StoredUser['status'],
                lastLogin: user.lastLogin || 'Never',
                avatar: user.avatar || (user.name || user.username).split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2),
                createdDate: user.createdAt || user.createdDate || new Date().toISOString()
            }));
            
            setUsers(apiUsers);
        } catch (error) {
            console.error('Error fetching users from API:', error);
            toast.error('Failed to load users from database');
            setUsers([]); // Set empty array if API fails
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (user: StoredUser) => {
        setSelectedUser(user);
        setEditForm({
            name: user.name,
            email: user.email,
            role: user.role,
            status: user.status
        });
        setIsEditModalOpen(true);
    };

    const handleDelete = (user: StoredUser) => {
        setDeleteModal({
            isOpen: true,
            itemId: user.id,
            itemName: user.name,
            isDeleting: false
        });
    };

    const confirmDelete = async () => {
        setDeleteModal(prev => ({ ...prev, isDeleting: true }));
        try {
            // If backend API exists for deleting admins, use it:
            await authAPI.deleteAdmin(deleteModal.itemId);
            
            // For now, use localStorage:
            userStorage.delete(deleteModal.itemId);
            loadUsers();
            toast.success(`User "${deleteModal.itemName}" has been deleted`);
            setDeleteModal({
                isOpen: false,
                itemId: '',
                itemName: '',
                isDeleting: false
            });
        } catch (error) {
            toast.error('Failed to delete user');
            setDeleteModal(prev => ({ ...prev, isDeleting: false }));
        }
    };

    const handleSaveEdit = () => {
        if (selectedUser) {
            const updatedUser: StoredUser = {
                ...selectedUser,
                name: editForm.name,
                email: editForm.email,
                role: editForm.role,
                status: editForm.status
            };
            userStorage.update(selectedUser.id, updatedUser);
            loadUsers();
            toast.success('User updated successfully');
            setIsEditModalOpen(false);
            setSelectedUser(null);
        }
    };

    const handleAddUser = () => {
        if (!editForm.name || !editForm.email || !editForm.role) {
            toast.error('Please fill in all required fields');
            return;
        }

        const newUser: Omit<StoredUser, 'id' | 'createdDate'> = {
            name: editForm.name,
            email: editForm.email,
            role: editForm.role,
            status: editForm.status || 'Active',
            lastLogin: 'Never',
            avatar: editForm.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
        };

        userStorage.add(newUser);
        loadUsers();
        toast.success('User added successfully');
        setIsAddModalOpen(false);
        setEditForm({ name: '', email: '', role: 'User', status: 'Active' });
    };

    const stats = [
        { 
            label: 'Total Users', 
            value: isLoading ? null : users.length.toString(), 
            icon: Users, 
            color: 'text-cyan-400', 
            bgColor: 'bg-cyan-400/10' 
        },
        { 
            label: 'Active Users', 
            value: isLoading ? null : users.filter(u => u.status === 'Active').length.toString(), 
            icon: Activity, 
            color: 'text-green-400', 
            bgColor: 'bg-green-400/10' 
        },
        { 
            label: 'Admins', 
            value: isLoading ? null : users.filter(u => u.role === 'Admin' || u.role === 'Super Admin').length.toString(), 
            icon: Shield, 
            color: 'text-purple-400', 
            bgColor: 'bg-purple-400/10' 
        },
    ];

    return (
        <AdminLayout>
            <AdminTopBar
                title="User Management"
                description="Manage system users and permissions"
                actionButton={{
                    label: 'Add New User',
                    icon: <UserPlus className="w-4 h-4 mr-2" />,
                    onClick: () => {
                        setEditForm({ name: '', email: '', role: 'User', status: 'Active' });
                        setIsAddModalOpen(true);
                    }
                }}
            />

            <div className="p-8 space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {stats.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="glass-strong rounded-xl border border-primary/30 p-6"
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground mb-1">{stat.label}</p>
                                        <p className="text-foreground text-2xl">{stat.value}</p>
                                    </div>
                                    <div className={`p-3 rounded-lg ${stat.bgColor} border border-primary/20`}>
                                        <Icon className={`w-6 h-6 ${stat.color}`} />
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Users List */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="glass-strong rounded-xl border border-primary/30 overflow-hidden"
                >
                    <div className="p-6 border-b border-primary/30">
                        <h3 className="text-foreground">All Users</h3>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-primary/30">
                                    <th className="text-left p-4 text-sm text-muted-foreground">User</th>
                                    <th className="text-left p-4 text-sm text-muted-foreground">Email</th>
                                    <th className="text-left p-4 text-sm text-muted-foreground">Role</th>
                                    <th className="text-left p-4 text-sm text-muted-foreground">Status</th>
                                    <th className="text-left p-4 text-sm text-muted-foreground">Last Login</th>
                                    <th className="text-left p-4 text-sm text-muted-foreground">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    // Loading skeleton rows
                                    Array.from({ length: 5 }).map((_, index) => (
                                        <tr key={index} className="border-b border-primary/10">
                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-muted/20 animate-pulse rounded-full"></div>
                                                    <div className="h-4 w-24 bg-muted/20 animate-pulse rounded"></div>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <div className="h-4 w-32 bg-muted/20 animate-pulse rounded"></div>
                                            </td>
                                            <td className="p-4">
                                                <div className="h-5 w-16 bg-muted/20 animate-pulse rounded"></div>
                                            </td>
                                            <td className="p-4">
                                                <div className="h-5 w-12 bg-muted/20 animate-pulse rounded"></div>
                                            </td>
                                            <td className="p-4">
                                                <div className="h-4 w-20 bg-muted/20 animate-pulse rounded"></div>
                                            </td>
                                            <td className="p-4">
                                                <div className="flex gap-2">
                                                    <div className="h-8 w-8 bg-muted/20 animate-pulse rounded"></div>
                                                    <div className="h-8 w-8 bg-muted/20 animate-pulse rounded"></div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : users.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="p-8 text-center text-muted-foreground">
                                            No users found. Add your first user to get started.
                                        </td>
                                    </tr>
                                ) : (
                                    users.map((user, index) => (
                                        <motion.tr
                                            key={user.id}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.4 + index * 0.1 }}
                                            className="border-b border-primary/10 hover:bg-primary/5 transition-colors"
                                        >
                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-linear-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                                                        <span className="text-white text-sm">
                                                            {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                                        </span>
                                                    </div>
                                                    <span className="text-foreground">{user.name}</span>
                                                </div>
                                            </td>
                                            <td className="p-4 text-muted-foreground">{user.email}</td>
                                            <td className="p-4">
                                                <span className={`px-3 py-1 rounded-full text-xs ${
                                                    user.role === 'Super Admin' 
                                                        ? 'bg-purple-400/10 text-purple-400 border border-purple-400/20'
                                                        : user.role === 'Admin'
                                                        ? 'bg-blue-400/10 text-blue-400 border border-blue-400/20'
                                                        : 'bg-green-400/10 text-green-400 border border-green-400/20'
                                                }`}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <span className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full ${
                                                        user.status === 'Active' 
                                                            ? 'bg-green-400 animate-pulse' 
                                                            : 'bg-red-400'
                                                    }`}></div>
                                                    <span className={`text-sm ${
                                                        user.status === 'Active' 
                                                            ? 'text-green-400' 
                                                            : 'text-red-400'
                                                    }`}>{user.status}</span>
                                                </span>
                                            </td>
                                            <td className="p-4 text-muted-foreground">
                                                {user.lastLogin === 'Never' ? 'Never' : new Date(user.lastLogin).toLocaleDateString('en-GB')}
                                            </td>
                                            <td className="p-4">
                                                <div className="flex gap-2">
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm" 
                                                        className="border-primary/30 hover:bg-primary/10 text-foreground"
                                                        onClick={() => handleEdit(user)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm" 
                                                        className="border-red-500/30 hover:bg-red-500/10 text-red-400"
                                                        onClick={() => handleDelete(user)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            </div>

            {/* Edit User Modal */}
            <AnimatePresence>
                {isEditModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="glass-strong rounded-xl border border-primary/30 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-foreground text-xl">Edit User</h3>
                                <button
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="text-sm text-muted-foreground mb-1 block">Name</label>
                                    <input
                                        type="text"
                                        value={editForm.name}
                                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                        className="w-full p-3 rounded-lg glass border border-primary/30 text-foreground focus:outline-none focus:border-primary"
                                    />
                                </div>

                                <div>
                                    <label className="text-sm text-muted-foreground mb-1 block">Email</label>
                                    <input
                                        type="email"
                                        value={editForm.email}
                                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                        className="w-full p-3 rounded-lg glass border border-primary/30 text-foreground focus:outline-none focus:border-primary"
                                    />
                                </div>

                                <div>
                                    <label className="text-sm text-muted-foreground mb-1 block">Role</label>
                                    <select
                                        value={editForm.role}
                                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value as StoredUser['role'] })}
                                        className="w-full p-3 rounded-lg glass border border-primary/30 text-foreground focus:outline-none focus:border-primary"
                                    >
                                        <option value="">Select Role</option>
                                        <option value="Super Admin">Super Admin</option>
                                        <option value="Admin">Admin</option>
                                        <option value="Moderator">Moderator</option>
                                        <option value="User">User</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="text-sm text-muted-foreground mb-1 block">Status</label>
                                    <select
                                        value={editForm.status}
                                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value as StoredUser['status'] })}
                                        className="w-full p-3 rounded-lg glass border border-primary/30 text-foreground focus:outline-none focus:border-primary"
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                        <option value="Suspended">Suspended</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <Button
                                    onClick={handleSaveEdit}
                                    className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                                >
                                    <Save className="w-4 h-4 mr-2" />
                                    Save Changes
                                </Button>
                                <Button
                                    onClick={() => setIsEditModalOpen(false)}
                                    variant="outline"
                                    className="flex-1 border-primary/30"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Add User Modal */}
            <AnimatePresence>
                {isAddModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="glass-strong rounded-xl border border-primary/30 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-foreground text-xl">Add New User</h3>
                                <button
                                    onClick={() => setIsAddModalOpen(false)}
                                    className="text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="text-sm text-muted-foreground mb-1 block">Name *</label>
                                    <input
                                        type="text"
                                        value={editForm.name}
                                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                        className="w-full p-3 rounded-lg glass border border-primary/30 text-foreground focus:outline-none focus:border-primary"
                                        placeholder="Enter user name"
                                    />
                                </div>

                                <div>
                                    <label className="text-sm text-muted-foreground mb-1 block">Email *</label>
                                    <input
                                        type="email"
                                        value={editForm.email}
                                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                        className="w-full p-3 rounded-lg glass border border-primary/30 text-foreground focus:outline-none focus:border-primary"
                                        placeholder="user@example.com"
                                    />
                                </div>

                                <div>
                                    <label className="text-sm text-muted-foreground mb-1 block">Role *</label>
                                    <select
                                        value={editForm.role}
                                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value as StoredUser['role'] })}
                                        className="w-full p-3 rounded-lg glass border border-primary/30 text-foreground focus:outline-none focus:border-primary"
                                    >
                                        <option value="">Select Role</option>
                                        <option value="Super Admin">Super Admin</option>
                                        <option value="Admin">Admin</option>
                                        <option value="Moderator">Moderator</option>
                                        <option value="User">User</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="text-sm text-muted-foreground mb-1 block">Status</label>
                                    <select
                                        value={editForm.status}
                                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value as StoredUser['status'] })}
                                        className="w-full p-3 rounded-lg glass border border-primary/30 text-foreground focus:outline-none focus:border-primary"
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                        <option value="Suspended">Suspended</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <Button
                                    onClick={handleAddUser}
                                    className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                                >
                                    <UserPlus className="w-4 h-4 mr-2" />
                                    Add User
                                </Button>
                                <Button
                                    onClick={() => setIsAddModalOpen(false)}
                                    variant="outline"
                                    className="flex-1 border-primary/30"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => !deleteModal.isDeleting && setDeleteModal({ isOpen: false, itemId: '', itemName: '', isDeleting: false })}
                onConfirm={confirmDelete}
                title="Delete User"
                message="Are you sure you want to delete this user?"
                itemName={deleteModal.itemName}
                isDeleting={deleteModal.isDeleting}
            />
        </AdminLayout>
    );
}
